import { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, inr, fmtDate, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, FileText, Trash2, MessageCircle, X, PackageCheck, Factory, Paperclip, Pencil, Send, RotateCcw, Mail } from "lucide-react";
import { QuoteAiEmailDialog } from "@/components/QuoteAiEmailDialog";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { SearchCombobox } from "@/components/SearchCombobox";
import { DatePicker } from "@/components/DatePicker";

const emptyLine = { product_id: "", product_code: "", description: "", color: "", cut_length: "", size: "", tc_size: "", hsn: "", quantity: 1, unit: "Meter", purchase_rate: "", margin_percent: "", unit_price: 0, gst: 18, use_stock: false, stock_qty: "", stock_id: "", _stock: null };

const fetchStockMatch = async (it) => {
  if (!/\d/.test(it.size || "")) return null;
  try {
    const r = await api.get("/size-intel/stock-match", { params: { size: it.size, color: it.color || "" } });
    return r.data.match || null;
  } catch { return null; }
};

export default function Quotations() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", lead_id: "", lead_number: "", date: "", transport: "", transporter_id: "", transporter_gstin: "", po_reference: "", po_date: "", items: [{ ...emptyLine }], freight: 0,
    terms: "100% Advance Against Pro Forma Invoice, Delivery Within 7 to 8 Working Days.", notes: "", internal_note: "" });
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([
    api.get("/quotations").then((r) => setList(r.data)),
    api.get("/customers").then((r) => setCustomers(r.data)),
    api.get("/products").then((r) => setProducts(r.data)),
    api.get("/leads").then((r) => setLeads(r.data)),
    api.get("/transporters").then((r) => setTransporters(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const quotedLeadIds = new Set(list.filter((q) => q.lead_id && q.id !== editing).map((q) => q.lead_id));

  const [searchParams, setSearchParams] = useSearchParams();
  const [qTab, setQTab] = useState("active");
  const [qSearch, setQSearch] = useUrlSearch();
  const productFilter = searchParams.get("product") || "";
  const visible = productFilter
    ? list.filter((q) => (q.items || []).some((it) => it.product_code === productFilter || it.description === productFilter))
    : list;
  const QUO_DONE = ["Converted", "Rejected"];
  const qNorm = qSearch.trim().toLowerCase();
  const searched = qNorm
    ? visible.filter((x) => [x.number, (x.customer_snapshot || {}).company_name, x.po_reference]
        .some((v) => String(v || "").toLowerCase().includes(qNorm)))
    : visible;
  const shown = searched.filter((x) => (qTab === "completed" ? QUO_DONE.includes(x.status) : !QUO_DONE.includes(x.status)));
  useEffect(() => {
    if (!qNorm) return;
    const done = searched.filter((x) => QUO_DONE.includes(x.status)).length;
    const act = searched.length - done;
    if (qTab === "active" && act === 0 && done > 0) setQTab("completed");
    if (qTab === "completed" && done === 0 && act > 0) setQTab("active");
  }, [qNorm, list]);
  const pager = usePager(shown);

  const totals = form.items.reduce((acc, it) => {
    const sub = (+it.quantity || 0) * (+it.unit_price || 0);
    acc.sub += sub; acc.gst += sub * (+it.gst || 0) / 100;
    if (form.in_house) {
      acc.profit += sub;
    } else if ((+it.purchase_rate || 0) > 0) {
      acc.cost += (+it.purchase_rate) * (+it.quantity || 0);
      acc.profit += ((+it.unit_price || 0) - (+it.purchase_rate)) * (+it.quantity || 0);
    }
    return acc;
  }, { sub: 0, gst: 0, cost: 0, profit: 0 });
  const freightGst = (+form.freight || 0) * 0.18;
  const total = totals.sub + totals.gst + freightGst + (+form.freight || 0);

  const viewPoFile = async (fid) => {
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open file"); }
  };

  const save = async () => {
    try {
      const hasLine = form.items.some((it) => (it.description || "").trim() && +it.quantity > 0 && +it.unit_price > 0);
      if (!hasLine) { toast.error("At least one product line with description, quantity and price is required — select a Lead to fill products"); return; }
      const body = {
        customer_id: form.customer_id,
        lead_id: form.lead_id || null, lead_number: form.lead_number || "",
        transport: form.transport, transporter_id: form.transporter_id, transporter_gstin: form.transporter_gstin,
        po_reference: form.po_reference,
        po_date: form.po_date || null,
        date: form.date || null,
        items: form.items.map(({ _stock, ...i }) => ({ ...i, quantity: +i.quantity, purchase_rate: +i.purchase_rate || 0, margin_percent: +i.margin_percent || 0, unit_price: +i.unit_price, gst: +i.gst, use_stock: !!i.use_stock, stock_qty: +i.stock_qty || 0, stock_id: i.stock_id || "" })),
        freight: +form.freight, terms: form.terms, notes: form.notes, internal_note: form.internal_note || "",
      };
      if (editing) await api.put(`/quotations/${editing}`, body);
      else await api.post("/quotations", body);
      toast.success("Quotation saved");
      setOpen(false); setEditing(null); resetForm(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetForm = () => setForm({ customer_id: "", lead_id: "", lead_number: "", in_house: false, date: "", transport: "", transporter_id: "", transporter_gstin: "", po_reference: "", po_date: "", items: [{ ...emptyLine }], freight: 0,
    terms: "100% Advance Against Pro Forma Invoice, Delivery Within 7 to 8 Working Days.", notes: "", internal_note: "" });
  const openNew = () => { setEditing(null); resetForm(); setOpen(true); };
  const refreshStocks = async (items) => {
    const withStock = await Promise.all(items.map(async (it) => ({ ...it, _stock: await fetchStockMatch(it) })));
    setForm((f) => ({ ...f, items: f.items.map((it, i) => withStock[i] ? { ...it, _stock: withStock[i]._stock } : it) }));
  };

  const edit = (q) => {
    setEditing(q.id);
    const items = q.items.length ? q.items.map((it) => ({ ...emptyLine, ...it })) : [{ ...emptyLine }];
    setForm({ customer_id: q.customer_id, lead_id: q.lead_id || "", lead_number: q.lead_number || "", in_house: !!q.in_house,
              customer_po_file_id: q.customer_po_file_id || "", customer_po_name: q.customer_po_name || "", customer_po_check: q.customer_po_check || null,
              transport: q.transport || "", transporter_id: q.transporter_id || "", transporter_gstin: q.transporter_gstin || "",
              po_reference: q.po_reference || "", po_date: q.po_date ? String(q.po_date).slice(0, 10) : "",
              date: q.date ? String(q.date).slice(0, 10) : "",
              items,
              freight: q.freight, terms: q.terms, notes: q.notes || "", internal_note: q.internal_note || "" });
    setOpen(true);
    refreshStocks(items);
  };

  const fillFromLead = (lid) => {
    const l = leads.find((x) => x.id === lid);
    if (!l) return;
    const items = (l.items || []).filter((it) => it.product_id).map((it) => {
      const p = products.find((x) => x.id === it.product_id);
      return { product_id: it.product_id, product_code: it.product_code, description: it.product_name,
               color: it.color || "", cut_length: it.cut_length || "", size: it.size_label || "", tc_size: it.tc_size_input || it.tc_size_label || "",
               hsn: p?.hsn || "",
               quantity: it.quantity, unit: it.unit,
               purchase_rate: it.purchase_rate || "", margin_percent: it.margin_percent || "",
               use_stock: !!it.use_stock, stock_qty: it.stock_qty || "", stock_id: it.stock_id || "", _stock: null,
               unit_price: it.sales_rate, gst: p?.gst ?? 18 };
    });
    setForm((f) => ({ ...f, lead_id: lid, lead_number: l.number || "", in_house: !!l.in_house,
      internal_note: l.internal_note || f.internal_note || "",
                      customer_id: l.customer_id || f.customer_id,
                      items: items.length ? items : f.items }));
    if (items.length) refreshStocks(items);
  };
  const setLine = (i, patch) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
  const setStatus = async (id, status) => {
    await api.patch(`/quotations/${id}/status`, null, { params: { status } }); load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/quotations/${id}`); load(); };
  const navigate = useNavigate();
  const [aiEmailFor, setAiEmailFor] = useState(null);
  const reorder = async (q) => {
    if (!window.confirm(`Reorder ${q.number} for ${q.customer_snapshot?.company_name || "this client"}? A new lead will be created with the same products.`)) return;
    try {
      const r = await api.post(`/quotations/${q.id}/reorder`);
      toast.success(`Lead ${r.data.lead_number} created from ${r.data.ref}`);
      navigate(`/leads?q=${encodeURIComponent(r.data.lead_number)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shareWhatsApp = async (q) => {
    try {
      const r = await api.get(`/quotations/${q.id}/share-link`);
      const link = `${API_URL}/public/quotations/${r.data.share_token}/pdf`;
      const msg = `Dear ${q.customer_snapshot?.company_name || "Customer"},\n\nPlease find our Quotation ${q.number} - ${q.customer_snapshot?.company_name || ""} for ${inr(q.total)}.\nView / download the PDF here: ${link}\n\nRegards,\nHi-Tech Silico\n+91 8888837157`;
      const digits = (q.customer_snapshot?.mobile || "").replace(/\D/g, "");
      const phone = digits.length === 10 ? `91${digits}` : digits;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="quotations-page">
      <PageHeader title="Quotations" subtitle="Generate & send GST-compliant quotes"
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="quo-new">
                <Plus size={14} className="mr-1" /> New Quotation
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-hidden flex flex-col content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Quotation</DialogTitle></DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pl-1 pr-4 space-y-3" data-testid="q-dialog-body">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Lead ID</Label>
                    <SearchCombobox testId="q-lead" placeholder="Select lead to auto-fill" searchPlaceholder="Search leads…" emptyText="No leads yet"
                      label={(() => { const l = leads.find((x) => x.id === form.lead_id); return l ? `${l.number ? `${l.number} — ` : ""}${l.company_name}` : (form.lead_number || ""); })()}
                      options={leads.map((l) => ({ id: l.id, label: `${l.number ? `${l.number} — ` : ""}${l.company_name}`,
                                                   disabled: quotedLeadIds.has(l.id), sub: quotedLeadIds.has(l.id) ? "Already quoted" : "" }))}
                      onSelect={fillFromLead} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Customer *</Label>
                    <SearchCombobox testId="q-customer" placeholder="Select customer" searchPlaceholder="Search customers…" emptyText="No customers yet"
                      label={customers.find((c) => c.id === form.customer_id)?.company_name}
                      options={customers.map((c) => ({ id: c.id, label: c.company_name, sub: c.city || "" }))}
                      onSelect={(v) => setForm({ ...form, customer_id: v })} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Transporter</Label>
                    <Select value={form.transporter_id} onValueChange={(v) => {
                      const t = transporters.find((x) => x.id === v);
                      setForm({ ...form, transporter_id: v, transport: t?.name || "", transporter_gstin: t?.gstin || "" });
                    }}>
                      <SelectTrigger className="mt-1.5" data-testid="q-transporter"><SelectValue placeholder="Select transporter" /></SelectTrigger>
                      <SelectContent>
                        {transporters.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        {transporters.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No transporters — add under Transporters</div>}
                      </SelectContent>
                    </Select>
                    {form.transporter_gstin && <p className="text-[11px] text-slate-500 mt-1 font-mono" data-testid="q-transporter-gst">Transporter GST: {form.transporter_gstin}</p>}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Quotation Date</Label>
                    <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} className="mt-1.5" testId="q-date" />
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Products <span className="normal-case font-normal text-slate-400">(from lead — read-only)</span></p>
                  {form.items.map((it, i) => {
                    const amt = (+it.quantity || 0) * (+it.unit_price || 0);
                    const salesRate = (+it.purchase_rate || 0) > 0 ? Math.round((+it.purchase_rate) * (1 + (+it.margin_percent || 0) / 100)) : 0;
                    const profit = form.in_house ? amt : ((+it.purchase_rate || 0) > 0 ? ((+it.unit_price || 0) - (+it.purchase_rate)) * (+it.quantity || 0) : 0);
                    const Meta = ({ label, value }) => value ? (
                      <span className="inline-flex items-center gap-1 text-xs">
                        <span className="text-slate-400">{label}</span>
                        <span className="text-slate-700 font-medium">{value}</span>
                      </span>
                    ) : null;
                    const Stat = ({ label, value, cls = "text-slate-800", tid }) => (
                      <div className="min-w-[86px]">
                        <div className="text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
                        <div className={`text-sm font-semibold tabular-nums ${cls}`} data-testid={tid}>{value}</div>
                      </div>
                    );
                    return (
                      <div key={i} className="border border-slate-200 rounded-lg p-4 bg-white hover:border-slate-300 transition-colors duration-150" data-testid={`q-item-${i}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {it.product_code && <span className="text-[10px] font-mono font-bold bg-[#13264D] text-white rounded px-1.5 py-0.5">{it.product_code}</span>}
                              <span className="font-semibold text-slate-800 text-sm">{it.description || "-"}</span>
                              {form.in_house && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-medium" data-testid={`q-line-${i}-inhouse`}>
                                  <Factory size={10} /> In-House
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                              <Meta label="Size" value={it.size} />
                              <Meta label="Color" value={it.color} />
                              <Meta label="Cut Length" value={it.cut_length} />
                              <Meta label="Notes" value={it.tc_size} />
                            </div>
                          </div>
                          <div className="text-right flex-none">
                            <div className="text-[9px] uppercase tracking-widest text-slate-400">Amount</div>
                            <div className="font-display font-bold text-slate-900 tabular-nums text-lg leading-tight">{inr(amt)}</div>
                          </div>
                        </div>
                        {it._stock && (
                          <div className="flex flex-wrap items-center gap-3 border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 mt-3" data-testid={`q-line-${i}-stock`}>
                            <PackageCheck size={14} className="text-emerald-600 shrink-0" />
                            <span className="text-xs text-emerald-800 font-medium">
                              In stock: {it._stock.quantity} {it._stock.unit} — {it._stock.product_name}{it._stock.color ? ` · ${it._stock.color}` : ""}
                            </span>
                            <label className="flex items-center gap-1.5 text-xs text-emerald-900 cursor-pointer">
                              <Checkbox checked={!!it.use_stock}
                                        onCheckedChange={(v) => setLine(i, { use_stock: !!v, stock_id: it._stock.id,
                                          stock_qty: v ? Math.min(+it._stock.quantity, +it.quantity || +it._stock.quantity) : "" })}
                                        data-testid={`q-line-${i}-usestock`} />
                              Use from stock
                            </label>
                            {it.use_stock && (
                              <span className="flex items-center gap-1.5 text-xs text-emerald-800">
                                <Input type="number" value={it.stock_qty}
                                       onChange={(e) => setLine(i, { stock_qty: Math.min(+e.target.value || 0, +it._stock.quantity) })}
                                       className="h-7 w-24 text-xs bg-white" data-testid={`q-line-${i}-stockqty`} />
                                from stock → vendor PO gets {Math.max(0, (+it.quantity || 0) - (+it.stock_qty || 0))} {it.unit}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 pt-3 border-t border-dashed border-slate-200">
                          <Stat label="Quantity" value={`${it.quantity} ${it.unit || ""}`} tid={`q-line-${i}-qty`} />
                          {!form.in_house && <Stat label="Purchase ₹" value={it.purchase_rate ? inr(+it.purchase_rate) : "—"} tid={`q-line-${i}-purchase`} />}
                          {!form.in_house && <Stat label="Margin" value={it.margin_percent ? `${it.margin_percent}%` : "—"} tid={`q-line-${i}-margin`} />}
                          <Stat label="Sales Rate" value={form.in_house ? inr(+it.unit_price || 0) : (salesRate ? inr(salesRate) : "—")} tid={`q-line-${i}-sales`} />
                          <Stat label={`Price / ${it.unit || "Unit"}`} value={inr(+it.unit_price || 0)} tid={`q-line-${i}-price`} />
                          <Stat label="Profit" value={profit ? inr(profit) : "—"} cls={profit < 0 ? "text-rose-600" : "text-emerald-700"} tid={`q-line-${i}-profit`} />
                          <Stat label="GST" value={`${it.gst}%`} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Freight (INR)</Label>
                    <Input type="number" value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} className="mt-1.5" />
                  </div>
                  <div className="col-span-2 flex flex-col items-end justify-end text-sm">
                    <div className="w-full max-w-xs space-y-1">
                      <Row l="Subtotal" v={totals.sub} />
                      <Row l={+form.freight > 0 ? "GST (incl. 18% on freight)" : "GST"} v={totals.gst + freightGst} />
                      <Row l="Freight" v={+form.freight || 0} />
                      <Row l="Total" v={total} bold />
                      {(totals.cost > 0 || (form.in_house && totals.profit > 0)) && (
                        <div className={`flex justify-between font-medium border-t border-dashed border-slate-200 pt-1 mt-1 ${totals.profit < 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid="q-order-profit">
                          <div>Order Profit ({form.in_house ? 100 : Math.round(totals.profit / totals.cost * 100)}%)</div>
                          <div className="tabular-nums">{inr(totals.profit)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Terms & Conditions</Label>
                  <Textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} rows={2} className="mt-1.5" />
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Internal Notepad</Label>
                    <span className="text-[10px] text-amber-600">(private — never shown to client)</span>
                  </div>
                  <Textarea value={form.internal_note || ""} onChange={(e) => setForm({ ...form, internal_note: e.target.value })}
                            rows={2} placeholder="e.g. 2% commission payable to Mr. X on this order"
                            className="mt-1.5 bg-amber-50/60 border-amber-200 focus-visible:ring-amber-300" data-testid="q-internal-note" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="q-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <QuoteAiEmailDialog quote={aiEmailFor} onClose={() => setAiEmailFor(null)} />

      {productFilter && (
        <div className="flex items-center gap-2 text-sm" data-testid="q-product-filter">
          <span className="text-slate-500">Showing quotations with product</span>
          <span className="inline-flex items-center gap-1.5 bg-[#3B82F6] text-white rounded-full pl-3 pr-1.5 py-1 text-xs font-medium">
            {productFilter}
            <button onClick={() => setSearchParams({}, { replace: true })} className="p-0.5 rounded-full hover:bg-white/20" title="Clear filter" data-testid="q-product-filter-clear">
              <X size={12} />
            </button>
          </span>
          <span className="text-slate-400 text-xs">({visible.length} of {list.length})</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="q-tabs">
          {[["active", "Active Quotations"], ["completed", "Completed Quotations"]].map(([v, l]) => (
            <button key={v} onClick={() => setQTab(v)} data-testid={`q-tab-${v}`}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors duration-200 ${qTab === v ? "bg-white shadow-sm font-semibold text-[#3B82F6]" : "text-slate-500 hover:text-slate-700"}`}>
              {l}
            </button>
          ))}
        </div>
        <Input value={qSearch} onChange={(e) => setQSearch(e.target.value)} placeholder="Search no., customer, PO ref…"
               className="h-9 w-64 bg-white" data-testid="q-search" />
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Number</th>
              <th className="text-left px-4 py-2.5 hidden lg:table-cell">Lead</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Date</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((q) => (
              <tr key={q.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`q-row-${q.id}`}>
                <td className="px-4 py-3 font-mono text-slate-700">{q.number}</td>
                <td className="px-4 py-3 hidden lg:table-cell font-mono text-xs text-slate-500">{q.lead_number || "-"}</td>
                <td className="px-4 py-3">{q.customer_snapshot?.company_name || "-"}</td>
                <td className="px-4 py-3 hidden md:table-cell">{fmtDate(q.date)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{inr(q.total)}</td>
                <td className="px-4 py-3">
                  <StatusPill status={q.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <a href={`${API_URL}/quotations/${q.id}/pdf`} target="_blank" rel="noreferrer"
                       className="p-1.5 text-slate-500 hover:text-[#2563EB] rounded hover:bg-slate-100 transition-colors duration-150" title="Quotation PDF" data-testid={`q-pdf-${q.id}`}><FileText size={14} /></a>
                    {q.customer_po_file_id && (
                      <button onClick={() => viewPoFile(q.customer_po_file_id)} className="p-1.5 text-emerald-600 hover:text-emerald-800 rounded hover:bg-emerald-50 transition-colors duration-150" title={`View uploaded Purchase Order${q.po_reference ? ` (${q.po_reference})` : ""}`} data-testid={`q-po-view-${q.id}`}><Paperclip size={14} /></button>
                    )}
                    <button onClick={() => shareWhatsApp(q)} className="p-1.5 text-emerald-600 hover:text-emerald-800 rounded hover:bg-emerald-50 transition-colors duration-150" title="Share on WhatsApp" data-testid={`q-whatsapp-${q.id}`}><MessageCircle size={14} /></button>
                    {q.status !== "Sent" && q.status !== "Converted" && <button onClick={() => setStatus(q.id, "Sent")} className="p-1.5 text-slate-500 hover:text-[#2563EB] rounded hover:bg-slate-100 transition-colors duration-150" title="Mark Sent" data-testid={`q-marksent-${q.id}`}><Send size={14} /></button>}
                    <button onClick={() => setAiEmailFor(q)} className="p-1.5 text-[#3B82F6] hover:text-[#2563EB] rounded hover:bg-blue-50 transition-colors duration-150" title="AI Email — draft & send to customer" data-testid={`q-ai-email-${q.id}`}><Mail size={14} /></button>
                    <button onClick={() => reorder(q)} className="p-1.5 text-indigo-600 hover:text-indigo-800 rounded hover:bg-indigo-50 transition-colors duration-150" title="Reorder — create a new lead with these products" data-testid={`q-reorder-${q.id}`}><RotateCcw size={14} /></button>
                    <button onClick={() => edit(q)} className="p-1.5 text-slate-500 hover:text-[#2563EB] rounded hover:bg-slate-100 transition-colors duration-150" title="Edit" data-testid={`q-edit-${q.id}`}><Pencil size={14} /></button>
                    <button onClick={() => del(q.id)} className="p-1.5 text-slate-500 hover:text-rose-600 rounded hover:bg-rose-50 transition-colors duration-150" title="Delete" data-testid={`q-del-${q.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-500">{productFilter ? "No quotations use this product." : (qTab === "completed" ? "No completed quotations yet." : "No active quotations.")}</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>
    </div>
  );
}

function Row({ l, v, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-display font-bold text-slate-900 border-t border-slate-300 pt-1 mt-1" : "text-slate-600"}`}>
      <div>{l}</div><div className="tabular-nums">{inr(v)}</div>
    </div>
  );
}

const STATUS_MAP = {
  "Draft": "bg-slate-100 text-slate-700 border-slate-200",
  "Sent": "bg-sky-50 text-sky-700 border-sky-200",
  "Accepted": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Rejected": "bg-rose-50 text-rose-700 border-rose-200",
  "Lost": "bg-slate-100 text-slate-600 border-slate-300",
  "Converted": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Confirmed": "bg-sky-50 text-sky-700 border-sky-200",
  "In Production": "bg-amber-50 text-amber-700 border-amber-200",
  "Ready": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Ready to Dispatch": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Dispatched": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Delivered": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Cancelled": "bg-rose-50 text-rose-700 border-rose-200",
  "Pending": "bg-slate-100 text-slate-700 border-slate-200",
  "In Transit": "bg-amber-50 text-amber-700 border-amber-200",
  "Unpaid": "bg-rose-50 text-rose-700 border-rose-200",
  "Partial": "bg-amber-50 text-amber-700 border-amber-200",
  "Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Overdue": "bg-rose-100 text-rose-800 border-rose-300",
  "Returned": "bg-slate-200 text-slate-600 border-slate-300 line-through",
  "QA": "bg-purple-50 text-purple-700 border-purple-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "On Hold": "bg-slate-100 text-slate-700 border-slate-200",
};

export function StatusPill({ status }) {
  const cls = STATUS_MAP[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{status}</span>;
}
